import { Button, HStack, VStack } from "@chakra-ui/react";
import {
  SyncState,
  FileImportState,
  SyncWalletErrorDetailCode,
  PopulatedWallet,
} from "@syla/shared/types/models/WalletBase";
import React, { useMemo } from "react";

import { parseSyncErrorMessage } from "../../../../../api/errors/parsers/parseSyncErrorMessage";
import { sortAssetHoldings } from "../../../../../helper/wallet/sortAssetHoldings";
import { walletStateProps } from "../../../../../helper/walletProps";
import { useReportError } from "../../../../../helpers/useReportError";
import { updateWallet } from "../../../../../store/actions/wallet";
import { useCurrentAccountStore } from "../../../../../store/currentAccountStore";
import { useValueHoldings } from "../../../../../store/useValueHoldings";
import { SubSectionHeading } from "../../../../atoms/Headings";
import { InfoToolTip } from "../../../../atoms/InfoToolTip";
import { getDataSourceStatusProps } from "../../getDataSourceStatusProps";
import { DataSourceErrorSection } from "./DataSourceErrorSection";
import { HoldingTable } from "./HoldingTable";
import { WalletDetails } from "./WalletDetails";

export const DataSourceExpanded = ({
  walletResponse,
  openEditDrawer,
}: {
  walletResponse: PopulatedWallet;
  openEditDrawer?: () => void;
}): JSX.Element => {
  const reportError = useReportError();
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const { holdingsLoading } = walletStateProps(walletResponse);

  const { data: holdingsValue } = useValueHoldings();

  // skip negative holdings when calculating allocation %
  const allocationTotalValue = useMemo(
    () =>
      holdingsValue?.getWalletTotals({
        walletId: walletResponse._id,
        skipNegative: true,
      })?.marketValue,
    [holdingsValue, walletResponse._id],
  );

  const statusProps = getDataSourceStatusProps(walletResponse);

  const syncErrorDetails =
    walletResponse.syncState === SyncState.Error &&
    walletResponse.syncError &&
    parseSyncErrorMessage(
      walletResponse.dataSource.syncAuthType,
      walletResponse.syncError,
      walletResponse.dataSource.type,
    );

  const walletDetailsProps = {
    importMethod: walletResponse.importMethod,
    importMethodName: statusProps.importMethodName,
    createdAt: walletResponse.createdAt,
    lastUpdated: statusProps.lastUpdated,
    syncState: statusProps.status,
    syncStateColor: statusProps.color,
    allowSync: walletResponse.allowSync,
    addresses: walletResponse.addresses,
  };

  return (
    <VStack
      alignItems="flex-start"
      w="full"
      p={{ base: 2, xs: 4 }}
      spacing={6}
      bgColor="gray.50"
    >
      {syncErrorDetails && (
        <DataSourceErrorSection
          errorDetails={syncErrorDetails}
          context={walletResponse}
          primaryActionButton={
            walletResponse.syncError?.code ===
              SyncWalletErrorDetailCode.INVALID_AUTH && (
              <Button variant="ghost" color="red.500" onClick={openEditDrawer}>
                Fix Access
              </Button>
            )
          }
        />
      )}
      {walletResponse.fileImportState === FileImportState.Error && (
        <DataSourceErrorSection
          errorDetails={{
            title: "Last Import Failed",
            message: "Your last import was unsuccessful",
          }}
          context={walletResponse}
          onClose={() => {
            // clear import state
            updateWallet(accountId, walletResponse._id, {
              fileImportState: null,
            }).catch(reportError);
          }}
        />
      )}
      <WalletDetails {...walletDetailsProps} />

      <VStack w="full" align="flex-start" spacing={4}>
        <HStack w="full">
          <SubSectionHeading>Holdings</SubSectionHeading>
          <InfoToolTip
            wrap
            label="The assets you currently hold on this data source, calculated from your imported transactions."
          />
        </HStack>
        {
          <HoldingTable
            isLoading={holdingsLoading || !walletResponse.holdings}
            holdings={sortAssetHoldings(walletResponse.holdings ?? [])}
            totalHoldingValue={allocationTotalValue}
          />
        }
      </VStack>
    </VStack>
  );
};
